<template>
  <DetailCard :title="$t('lengths')" :style="{ borderRadius: '0px 4px 4px 4px' }">
    <template #title.append>
      <v-row
      v-if="crud"
      no-gutters
      justify="end"
      class="mx-n2">
        <Icon
        icon="mdi-plus-box-multiple-outline"
        :small="false"
        :tooltipText="$t('addRange')"
        @icon-clicked="addRangeClicked"/>

        <v-menu
        v-model="addLogLengthOpen"
        :close-on-content-click="false"
        close-on-click
        offset-y>
          <LogLengthForm
          id="create-account-log-length-form-menu"
          v-if="addLogLengthOpen"
          :existingLengths="accountLogLengths"
          @mutate-log-length="mutateLogLength"
          isScaling/>
          <template #activator="menu">
            <v-btn
            color="primary"
            icon
            v-on="{...menu.on}"
            @click="newLengthClicked()"
            class="my-n2">
              <v-icon>
                mdi-plus
              </v-icon>
            </v-btn>
          </template>
        </v-menu>
      </v-row>
    </template>

    <div id="account-log-lengths">
      <NoData v-if="noData"><div class="my-n3">{{ $t('none') }}</div></NoData>
      <v-chip
      v-for="(length, index) in accountLogLengths"
      :key="index"
      :color="selectedLengths.includes(length.length) ? 'primary' : ''"
      label
      outlined
      :close="crud && lengthNotInUse(length)"
      class="mr-1 mb-1"
      :class="selectedLengths.includes(length.length) ? 'selected-length' : 'grey-chip'"
      :ripple="false"
      @click="onLengthClicked(length)"
      @click:close="removeLength(length)">
        <span>
          {{ `${parseInt(length.length)}'` }}
        </span>
      </v-chip>
    </div>

    <Dialog :stateId="dialogId" @close="closeDialogs">
      <AccountLogLengthsRangeForm
      v-if="logLengthRangeDialogOpen"
      :accountId="accountId"
      @creation-successful="closeDialogs"/>
    </Dialog>
  </DetailCard>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { helpers as colorHelpers } from '@/plugins/theme'
import fieldRules from '@/utils/rules.js'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'AccountLogLengths',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    LogLengthForm: () => import('@/components/settings-components/company-config/log-lengths/LogLengthForm.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    AccountLogLengthsRangeForm: () => import('./AccountLogLengthsRangeForm.vue')
  },

  props: {
    crud: {
      type: Boolean,
      required: false,
      default: false
    },

    account: {
      type: Object,
      required: true
    },

    selectedLengths: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  async mounted () {
    await this.refresh()
  },

  data: () => ({
    rules: fieldRules.rules,
    colorHelpers,
    dialogId: uniqueDialogId('account-scaling-specs-form-dialog'),
    loading: true,
    addLogLengthOpen: false,
    logLengthRangeDialogOpen: false
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountLogLengths', 'accountLengthGroups']),

    noData () {
      return this.accountLogLengths?.length === 0
    },

    accountId () {
      return this.account?.accountId || null
    }
  },

  watch: {
    accountLogLengths: {
      handler () {
        this.removeTabindexFromCloseButtons()
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('account-scaling', ['fetchAccountLogLengths', 'createAccountLogLengths', 'deleteAccountLogLength']),

    async refresh () {
      this.loading = true
      if (this.accountId) {
        await this.fetchLogLengths()
      }
      this.loading = false
    },

    async fetchLogLengths () {
      await this.fetchAccountLogLengths(this.accountId)
    },

    newLengthClicked () {
      this.addLogLengthOpen = true
    },

    lengthNotInUse (length) {
      return this.accountLengthGroups
        ?.every(lg => lg.logLengths
          ?.every(ll => ll.length !== length.length))
    },

    mutateLogLength ({ newLength, _, __, confirmationMethod }) {
      if (confirmationMethod !== 'comma') {
        this.addLogLengthOpen = false
      }
      this.pushNewLength(newLength)
    },

    async removeLength (lengthToRemove) {
      await this.deleteLengthFromServer(lengthToRemove.logLengthId)
      await this.fetchAccountLogLengths(this.accountId)
    },

    async pushNewLength (newLength) {
      await this.addLengthOnServer(newLength.length)
    },

    async deleteLengthFromServer (id) {
      return await this.deleteAccountLogLength({ accountId: this.accountId, logLengthId: id })
    },

    async addLengthOnServer (length) {
      return await this.createAccountLogLengths({ accountId: this.accountId, logLengths: [length] })
    },

    closeDialogs () {
      this.logLengthRangeDialogOpen = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: 600 })
    },

    removeTabindexFromCloseButtons () {
      this.$nextTick(() => {
        const closeButtons = document.querySelectorAll('#account-log-lengths .v-chip__close')
        closeButtons.forEach(btn => btn.setAttribute('tabindex', '-1'))
      })
    },

    addRangeClicked () {
      this.logLengthRangeDialogOpen = true
      this.openDialog()
    },

    onLengthClicked (length) {
      const idx = this.selectedLengths.indexOf(length.length)
      const updated = [...this.selectedLengths]
      if (idx === -1) {
        updated.push(length.length)
      } else {
        updated.splice(idx, 1)
      }
      this.$emit('update:selectedLengths', updated)
    }
  }
}
</script>

<style scoped>
  .grey-chip:hover {
    cursor: pointer !important;
  }
  .v-chip.v-chip--outlined.selected-length {
    background-color: #d15f27 !important;
    color: #fff !important;
  }
  .v-chip.v-chip--outlined.selected-length:hover {
    cursor: pointer !important;
  }
</style>
